/**
 * useSeo — zero-dependency per-route SEO metadata.
 *
 * CRA renders a single static index.html, so every route otherwise shares one
 * <title>/description. This hook imperatively sets the document title, meta
 * description, canonical URL, and Open Graph / Twitter title+description for the
 * current page on mount. Googlebot executes client JS and indexes the updated
 * tags, so this materially improves per-page search results without an SSR
 * framework migration. (Non-JS social crawlers still get the static defaults from
 * index.html — full per-page unfurls + per-ticker long-tail pages are the larger
 * prerender/SSR item tracked in ROADMAP "SEO".)
 */
import { useEffect } from "react";

const SITE_NAME = "DejaChart";
const ORIGIN = "https://dejachart.com";

interface SeoOptions {
  title: string;            // page title (site name is appended automatically)
  description?: string;
  /** Path for the canonical URL, e.g. "/about". Defaults to the current path. */
  path?: string;
  /**
   * Emit `<meta name="robots" content="noindex">` for pages that must not rank —
   * e.g. an admin-gated route whose API 401s for the public. Cleared on unmount so
   * the flag can never leak onto the next (indexable) route in this SPA.
   */
  noindex?: boolean;
}

function upsertMeta(selector: string, attr: "name" | "property", key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSeo({ title, description, path, noindex }: SeoOptions): void {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonical = `${ORIGIN}${path ?? window.location.pathname}`;

    document.title = fullTitle;
    upsertMeta('meta[property="og:title"]', "property", "og:title", fullTitle);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", fullTitle);
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
    upsertCanonical(canonical);

    if (description) {
      upsertMeta('meta[name="description"]', "name", "description", description);
      upsertMeta('meta[property="og:description"]', "property", "og:description", description);
      upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    }

    if (!noindex) return;
    upsertMeta('meta[name="robots"]', "name", "robots", "noindex");
    return (): void => {
      document.head.querySelector('meta[name="robots"]')?.remove();
    };
  }, [title, description, path, noindex]);
}
